import { Component, Input, OnInit } from '@angular/core'
import { FormBuilder, FormControl, Validators } from '@angular/forms'
import { select, Store } from '@ngrx/store'
import { AmplitudeService } from '@tv3/services/amplitude.service'
import { ConfirmDialogService, selectUsersByRole, untilDestroy, USER_ROLES } from '@tokeet-frontend/tv3-platform'
import {
  selectTemplateById,
  selectTemplatesForAutomationEvent,
  TemplateService,
  TemplateType,
} from '@tokeet-frontend/templates'
import { AuthService } from '@tv3/services/auth.service'
import { NotifyAutomationBaseComponent } from '../notify-automatotion.component'
import { filter, map, mapTo, switchMap } from 'rxjs/operators'
import { Actions, ofType } from '@ngrx/effects'
import { AutomationHandler, AutomationTypes, selectAutomationGroupsByType } from '@tokeet-frontend/automations'
import * as lodash from 'lodash'
import { combineLatest } from 'rxjs'
import {
  AutomationWizardCloseAction,
  AutomationWizardSaveAction,
} from '@tv3/containers/automations/automations-wizard/automations-wizard.actions'

@Component({
  selector: 'app-notify-users-lock-automation',
  templateUrl: './notify-users-lock-automation.component.html',
  styleUrls: ['./notify-users-lock-automation.component.scss'],
})
export class NotifyUsersLockAutomationComponent extends NotifyAutomationBaseComponent implements OnInit {
  @Input() type = AutomationTypes.Device
  @Input() defaultToAdmins = false

  // hide the inputs
  filterByCategories = false
  categories = []
  lateBookings = false
  constructor(
    private auth: AuthService,
    protected actions: Actions,
    protected store: Store<any>,
    protected amplitudeService: AmplitudeService,
    protected confirm: ConfirmDialogService,
    protected templateService: TemplateService
  ) {
    super(actions, store, amplitudeService, templateService, confirm)
    this.form.addControl('users', new FormControl([], [Validators.required]))
  }

  ngOnInit(): void {
    // super.ngOnInit()  // don't call super

    this.form
      .get('status')
      .valueChanges.pipe(
        switchMap((value) =>
          this.confirm
            .confirm({
              title: value ? $localize`Enable this Automation?` : $localize`Disable this Automation?`,
              body: value
                ? $localize`Do you want to enable this automation?`
                : $localize`Do you want to disable this automation?`,
            })
            .pipe(mapTo(value))
        ),
        untilDestroy(this)
      )
      .subscribe((value) => {
        this.tryToCreateOrUpdateAutomation(value)
      })

    this.automations$ = this.store.pipe(
      select(selectAutomationGroupsByType(this.type, false)),
      map((items) => items.filter((item) => item.service === this.service && item.event === this.event)),
      map((items) => lodash.filter(items, (item) => item.attributes?.autopilot === this.getAutopilotId()))
    )

    this.automations$.pipe(untilDestroy(this)).subscribe((items) => {
      this.automationGroups = items
    })

    this.automation$ = this.automations$.pipe(map((items) => lodash.head(items)))
    this.automation$.pipe(untilDestroy(this)).subscribe((item) => {
      this.automationGroup = item
    })

    combineLatest([
      this.automation$.pipe(switchMap((item) => this.store.pipe(select(selectTemplateById(item?.template))))),
      this.store.pipe(
        select(selectTemplatesForAutomationEvent(this.event)),
        map((items) =>
          items.filter((t) => t.type === TemplateType.Email && t.attributes?.autopilot === this.getAutopilotId())
        ),
        map((items) => lodash.head(items))
      ),
    ])
      .pipe(untilDestroy(this))
      .subscribe(([automationTemplate, template]) => {
        automationTemplate = automationTemplate || template
        this.template = automationTemplate
      })

    this.actions
      .pipe(
        ofType(AutomationWizardSaveAction),
        filter(() => this.viewType !== 'table'),
        untilDestroy(this)
      )
      .subscribe((action) => {
        if (this.automationGroup) {
          this.onSave(this.form, action.closeAfterSave)
        } else if (action.closeAfterSave) {
          this.store.dispatch(AutomationWizardCloseAction())
        }
      })

    combineLatest([this.automation$, this.store.pipe(select(selectUsersByRole(USER_ROLES.admin.value)))])
      .pipe(untilDestroy(this))
      .subscribe(([item, admins]) => {
        this.handler = item?.handler || AutomationHandler.SendEmailMessage
        this.form.patchValue(
          {
            status: !!item?.status,
            users: item?.users || (this.defaultToAdmins ? admins.map((u) => u.id) : [this.auth.user.id]),
          },
          { emitEvent: false }
        )
      })
  }

  getAutopilotId() {
    return `notify-users-lock|${this.service}|${this.type}|${this.event}|${this.idSuffix || ''}`
  }
}
